import { any, chain, compose, defaultTo, equals, find, includes, isNotNil, isNil, join, map, pathOr, prop, propEq, propOr, reduce, reject, split, uniq, values, assoc } from 'ramda';
import { ROLES, TEAM_ROLES } from '../../../lib/rights';
import { SCOPE_OFFER } from '../../../_CONST';

export const getOfferIds = compose(reject(equals('')), split(','), String, defaultTo(''));

export const offerIdIncludes = (offerIds, offerIdsToCheck) => {
  const normalizedOfferIds = getOfferIds(offerIds);
  const normalizedOfferIdsToCheck = getOfferIds(offerIdsToCheck);

  return any((offerId) => includes(offerId, normalizedOfferIdsToCheck), normalizedOfferIds);
};

export const RoleActionEnum = {
  ADD: 'add',
  DELETE: 'delete'
};

export const RoleValueEnum = {
  ALL: 'all',
  EMPTY: 'empty',
  PENDING: 'pending'
};

export const RoleLabelEnum = {
  ALL_TEAMS: 'Toutes les équipes',
  WITHOUT_TEAMS: 'Sans équipe',
  ALL_OFFERS: 'Toutes les offres',
  WITHOUT_OFFERS: 'Sans offre',
  ALL_ROLES: 'Tous les rôles',
  WITHOUT_ROLES: 'Sans rôle',
  PENDING: 'En attente'
};

export const ARCHIVED_LABEL_SUFFIX = '(archivée)';

export const aggregateAdminRoles = (role, acc, offerId, offers, teamRole) => {
  const count = pathOr(0, [role, 'count'], acc) + 1;
  const offerName = propOr('', 'offerName', find(propEq(offerId, 'id'))(offers));
  const existingOfferNames = pathOr([], [role, 'offerNames'], acc);

  if (!includes(offerName, existingOfferNames) || isNil(offerId)) {
    acc[role] = {
      ...teamRole,
      count,
      offerNames: [...existingOfferNames, offerName]
    };
  }

  return acc;
};

export const groupUserRoles = (role, acc, offerId, options) => {
  const matchingOfferOption = find(({ value }) => offerIdIncludes(value, offerId), options);
  const normalizedOfferId = propOr(offerId, 'value', matchingOfferOption);
  const name = prop('label', matchingOfferOption);
  const existingRole = find(propEq(name, 'name'), acc);

  if (existingRole) {
    const mergedOfferId = compose(join(','), uniq, chain(getOfferIds))([prop('offerId', existingRole), normalizedOfferId]);
    return map(
      (roleAcc) => equals(roleAcc, existingRole)
        ? assoc('offerId', mergedOfferId, roleAcc)
        : roleAcc,
      acc
    );
  } else {
    return [...acc, {
      role, offerId: normalizedOfferId, name 
    }];
  }
};

const processRoles = (roles, isAdministration, items, processFn) => {
  const baseProcessedRoles = isAdministration ? {} : [];
  const processedRoles = reduce((acc, { role, offerId }) => {
    const teamRole = find(propEq(role, 'value'), TEAM_ROLES);
    if (!teamRole) return acc;
    return processFn(role, acc, offerId, items, teamRole);
  }, baseProcessedRoles)(roles);
  return isAdministration ? values(processedRoles) : processedRoles;
};

export const mapToTeamRoles = (isAdministration, items, isSuperAdmin) => (team) => {
  const roles = isSuperAdmin ? [] : prop('roles', team);
  const processFn = isAdministration ? aggregateAdminRoles : groupUserRoles;
  const processedRoles = processRoles(roles, isAdministration, items, processFn);
  const teamRoles = isAdministration
    ? processedRoles
    : map(({ role }) => find(propEq(role, 'value'), TEAM_ROLES))(processedRoles);

  const result = {
    ...team, roles, teamRoles 
  };

  if (!isAdministration) result.roles = processedRoles;
  return result;
};

export const isAdminRole = (roles) => find((role) => propEq(ROLES.admin, 'role', role) || propEq(ROLES.admin, 'roleValue', role), roles);

export const isSuperAdminUserRole = (user) => includes(ROLES.superAdmin, propOr([], 'roles', user));

export const getRolesToDisplayed = (roles) => reduce((acc, { offer, uuid, teamId, ...rest }) => {
  const existingOffer = find(item => propEq(offer, 'offer', item) && propEq(teamId, 'teamId', item))(acc);
  if (existingOffer) {
    existingOffer.uuids.push(uuid);
  } else {
    acc.push({
      offer, uuids: [uuid], teamId, ...rest 
    });
  }
  return acc;
}, [], roles);

export const hasRole = (roleToCheck, roles, offerIdToCheck) => compose(
  isNotNil,
  find((role) => {
    if (!propEq(roleToCheck, 'role', role)) return false;
    if (isNil(offerIdToCheck) || equals('', offerIdToCheck)) return true;

    return offerIdIncludes(prop('offerId', role), offerIdToCheck);
  })
)(roles);

export const canEditOfferItem = ({ userIsAdmin, userIsOfferEditor, scope, isDraft }) =>
  userIsAdmin || (equals(scope, SCOPE_OFFER) && userIsOfferEditor && isDraft);
